import React, { useState, useEffect } from 'react';
import { signup } from '../api/ideas';
import { FaChevronDown, FaChevronUp, FaKey } from 'react-icons/fa';
import axiosInstance from '../api/api';

function SignupModal({ closeModal, openLoginModal, initialInviteToken = '', currentToken = null, onSuccessNavigate = null }) {
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [inviteToken, setInviteToken] = useState(initialInviteToken);
  const [error, setError] = useState('');
  const [showInviteSection, setShowInviteSection] = useState(false);
  const [inviteTokenValid, setInviteTokenValid] = useState(null); // null = not checked, true = valid, false = invalid
  const [checkingInvite, setCheckingInvite] = useState(false);

  useEffect(() => {
    if (initialInviteToken) {
      setInviteToken(initialInviteToken);
      setShowInviteSection(false); // Keep closed by default, even with token
      validateInviteToken(initialInviteToken);
    }
  }, [initialInviteToken]);

  // Function to validate invite token
  const validateInviteToken = async (token) => {
    if (!token || token.trim() === '') {
      setInviteTokenValid(null);
      return;
    }

    setCheckingInvite(true);
    try {
      const response = await axiosInstance.get(`/invites/${token.trim()}`);
      setInviteTokenValid(true);
    } catch (err) {
      console.error('Invite validation error:', err);
      setInviteTokenValid(false);
    } finally {
      setCheckingInvite(false);
    }
  };

  // Debounce invite token validation
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      validateInviteToken(inviteToken);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [inviteToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password !== passwordConfirm) {
      setError('Passwords do not match');
      return;
    }
    
    // Require invite token for signup
    if (!inviteToken || inviteToken.trim() === '') {
      setError('Invite token is required for signup');
      return;
    }
    
    if (inviteTokenValid === false) {
      setError('Please provide a valid invite token');
      return;
    }
    
    try {
      await signup(username, displayName, password, passwordConfirm, inviteToken);
      // Close signup modal first
      closeModal();
      // Navigate away from signup URL if we're on it
      if (onSuccessNavigate) {
        onSuccessNavigate();
      }
      // Then open login modal
      setTimeout(() => {
        openLoginModal();
      }, 100); // Small delay to ensure smooth transition
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[var(--bg-secondary)] p-6 rounded-xl shadow-lg max-w-md w-full">
        <h3 className="text-lg font-semibold mb-4 text-[var(--text-primary)]">Sign Up</h3>
        
        {currentToken && (
          <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-2 rounded-lg mb-4">
            <p className="text-sm">You're already logged in. If you want to create a new account, please log out first.</p>
          </div>
        )}
        
        {error && (
          <div className="bg-danger-light border text-danger px-4 py-2 rounded-lg mb-4">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <input
              type="text"
              className="w-full p-2 border border-gray-300 dark:border-slate-600 rounded-lg text-[var(--text-primary)] bg-[var(--bg-secondary)] focus:ring-2 focus:ring-[var(--primary)]"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          <div className="mb-4">
            <input
              type="text"
              className="w-full p-2 border border-gray-300 dark:border-slate-600 rounded-lg text-[var(--text-primary)] bg-[var(--bg-secondary)] focus:ring-2 focus:ring-[var(--primary)]"
              placeholder="Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div className="mb-4">
            <input
              type="password"
              className="w-full p-2 border border-gray-300 dark:border-slate-600 rounded-lg text-[var(--text-primary)] bg-[var(--bg-secondary)] focus:ring-2 focus:ring-[var(--primary)]"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="mb-4">
            <input
              type="password"
              className="w-full p-2 border border-gray-300 dark:border-slate-600 rounded-lg text-[var(--text-primary)] bg-[var(--bg-secondary)] focus:ring-2 focus:ring-[var(--primary)]"
              placeholder="Confirm Password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
            />
          </div>
          
          {/* Collapsible Invite Token Section */}
          <div className="mb-4">
            <button
              type="button"
              onClick={() => setShowInviteSection(!showInviteSection)}
              className="w-full flex items-center justify-between p-3 border border-gray-300 dark:border-slate-600 rounded-lg bg-[var(--bg-secondary)] hover:bg-[var(--bg-hover)] transition-colors"
            >
              <div className="flex items-center gap-2">
                <FaKey className="text-[var(--text-secondary)]" />
                <span className="text-[var(--text-primary)]">
                  {inviteToken ? 'Invite Token (Provided)' : 'Have an Invite Token?'}
                </span>
              </div>
              {showInviteSection ? <FaChevronUp /> : <FaChevronDown />}
            </button>
            
            {showInviteSection && (
              <div className="mt-2 p-3 border border-gray-200 dark:border-slate-700 rounded-lg bg-[var(--bg-primary)]">
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 dark:border-slate-600 rounded-lg text-[var(--text-primary)] bg-[var(--bg-secondary)] focus:ring-2 focus:ring-[var(--primary)]"
                  placeholder="Enter your invite token"
                  value={inviteToken}
                  onChange={(e) => setInviteToken(e.target.value)}
                />
                {checkingInvite && (
                  <p className="text-xs text-blue-600 mt-1">
                    Validating invite token...
                  </p>
                )}
                {!checkingInvite && inviteToken && inviteTokenValid === true && (
                  <p className="text-xs text-success mt-1">
                    ✓ Valid invite token
                  </p>
                )}
                {!checkingInvite && inviteToken && inviteTokenValid === false && (
                  <p className="text-xs text-danger mt-1">
                    ✗ Invalid or expired invite token
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-[var(--primary)] text-white rounded-lg text-sm hover:bg-opacity-80 transition-colors"
            >
              Sign Up
            </button>
            <button
              type="button"
              className="px-4 py-2 bg-[var(--secondary)] text-white rounded-lg text-sm hover:bg-opacity-80 transition-colors"
              onClick={closeModal}
            >
              Cancel
            </button>
          </div>
          
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => {
                closeModal();
                openLoginModal();
              }}
              className="text-sm text-[var(--primary)] hover:underline"
            >
              Already have an account? Sign in here
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SignupModal;